import { Command } from '@/plugins/cad/module/core/Command'
import type { CADViewManager } from '../manager/CADViewManager'
import type { IEntity } from '../model/entity'

/**
 * 修剪实体命令
 * 将被修剪的原实体替换为修剪后生成的新实体（0、1 或 2 个片段）
 */
export class TrimEntityCommand extends Command {
  private readonly data: {
    original: IEntity[]
    created: IEntity[]
  }[] = []

  constructor(private readonly viewManager: CADViewManager) {
    super()
  }

  /** 实际修剪在 addEntity 中完成，这里为空实现 */
  execute(): void {
    // 已在工具中处理实体修改，这里不再执行具体操作
  }

  /** 撤销：移除所有修剪片段并恢复原实体 */
  undo(): void {
    const { original, created } = this.data.reduce(
      (acc, item) => {
        acc.original.push(...item.original)
        acc.created.push(...item.created)
        return acc
      },
      { original: [] as IEntity[], created: [] as IEntity[] }
    )
    this.viewManager.deleteEntities(created)
    this.viewManager.addEntities(original)
  }

  /** 重做：重新以修剪片段替换原实体 */
  redo(): void {
    const { original, created } = this.data.reduce(
      (acc, item) => {
        acc.original.push(...item.original)
        acc.created.push(...item.created)
        return acc
      },
      { original: [] as IEntity[], created: [] as IEntity[] }
    )
    this.viewManager.deleteEntities(original)
    this.viewManager.addEntities(created)
  }

  /** 分步撤销：回退最近一次修剪 */
  undoStep() {
    if (!this.canUndoStep()) {
      return
    }
    const { original, created } = this.data.pop()!
    this.viewManager.deleteEntities(created)
    this.viewManager.addEntities(original)
  }

  /**
   * 是否支持分步撤销。
   *
   * @returns 存在已记录的修剪操作时返回 true
   */
  canUndoStep(): boolean {
    return this.data.length > 0
  }

  /**
   * 是否允许提交命令。
   *
   * @returns 至少完成过一次修剪时返回 true
   */
  canCommit(): boolean {
    return this.data.length > 0
  }

  /**
   * 记录并执行一次修剪：移除 original、加入 created。
   *
   * @param original - 被修剪的原实体集合
   * @param created - 修剪后新增的片段集合，可能为空
   */
  addEntity(original: IEntity[], created: IEntity[]) {
    this.data.push({ original: original, created })
    this.viewManager.deleteEntities(original)
    this.viewManager.addEntities(created)
  }
}
